import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  Save,
  Refresh,
  ExpandMore,
  Settings,
  EmojiEvents,
  Schedule,
  MonetizationOn,
  Info,
  Warning,
} from '@mui/icons-material';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { motion } from 'framer-motion';

const CompetitionSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [settings, setSettings] = useState({
    // Competition Status
    competitionActive: true,
    registrationOpen: true,
    leaderboardVisible: true,
    
    // Competition Dates
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    
    // Prize Configuration
    xmPointsPrizes: {
      first: 'R250,000',
      second: 'R150,000',
      third: 'R100,000',
      fourth: 'R50,000',
      fifth: 'R30,000',
      sixth: 'R25,000',
      seventh: 'R25,000',
      eighth: 'R25,000',
      ninth: 'R25,000',
      tenth: 'R20,000'
    },
    xmPercentagePrizes: {
      first: 'R50,000',
      second: 'R30,000',
      third: 'R20,000'
    },
    weltradePrizes: {
      first: 'R70,000',
      second: 'R50,000',
      third: 'R30,000',
      fourth: 'R25,000',
      fifth: 'R20,000'
    },
    monthlyExcellencePrize: 'R50,000',
    
    // Contact Information
    supportEmail: 'support@t1fxteam.co.za',
    whatsappNumber: '+27 071 394 4924',
    
    // System Settings
    maxRegistrations: 10000,
    emailNotifications: true,
    autoLeaderboardUpdate: true,
    
    // Competition Rules
    minTradingDays: 15,
    minTradingVolume: 1.0,
    verificationRequired: true,
    
    // Messages and Announcements
    welcomeMessage: 'Welcome to FX Wars - South Africa\'s premier trading competition!',
    maintenanceMode: false,
    maintenanceMessage: 'System is currently under maintenance. Please check back later.',
    announcementText: '',
    announcementActive: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const settingsDoc = await getDoc(doc(db, 'system', 'competition_settings'));
      if (settingsDoc.exists()) {
        setSettings(prev => ({ ...prev, ...settingsDoc.data() }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showSnackbar('Error fetching settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'system', 'competition_settings'), {
        ...settings,
        lastUpdated: new Date(),
        updatedBy: 'admin' // You could get this from auth context
      });
      showSnackbar('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      showSnackbar('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSettingChange = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      if (path.includes('.')) {
        const [parent, child] = path.split('.');
        newSettings[parent] = { ...newSettings[parent], [child]: value };
      } else {
        newSettings[path] = value;
      }
      return newSettings;
    });
  };

  const getCompetitionStatus = () => {
    if (settings.maintenanceMode) return { status: 'Maintenance', color: 'error' };
    if (!settings.competitionActive) return { status: 'Inactive', color: 'default' };
    if (!settings.registrationOpen) return { status: 'Registration Closed', color: 'warning' };
    return { status: 'Active', color: 'success' };
  };

  const statusInfo = getCompetitionStatus();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Competition Settings
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
              Current Status:
            </Typography>
            <Chip 
              label={statusInfo.status} 
              color={statusInfo.color}
              icon={<EmojiEvents />}
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<Refresh />}
            variant="outlined"
            onClick={fetchSettings}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            startIcon={<Save />}
            variant="contained"
            onClick={saveSettings}
            disabled={saving || loading}
          >
            Save Changes
          </Button>
        </Stack>
      </Box>

      {/* System Status Alert */}
      {settings.maintenanceMode && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Maintenance Mode Active:</strong> The competition is currently in maintenance mode. 
          Users will see the maintenance message instead of normal content.
        </Alert>
      )}

      {/* Settings Sections */}
      <Stack spacing={3}>
        {/* Competition Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <Settings sx={{ mr: 1 }} />
                Competition Status & Control
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settings.competitionActive}
                        onChange={(e) => handleSettingChange('competitionActive', e.target.checked)}
                      />
                    }
                    label="Competition Active"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Master switch for the entire competition
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settings.registrationOpen}
                        onChange={(e) => handleSettingChange('registrationOpen', e.target.checked)}
                      />
                    }
                    label="Registration Open"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Allow new user registrations
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settings.leaderboardVisible}
                        onChange={(e) => handleSettingChange('leaderboardVisible', e.target.checked)}
                      />
                    }
                    label="Leaderboard Visible"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Show leaderboard to public
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settings.maintenanceMode}
                        onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                      />
                    }
                    label="Maintenance Mode"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Enable maintenance mode (overrides all other settings)
                  </Typography>
                  {settings.maintenanceMode && (
                    <TextField
                      fullWidth
                      label="Maintenance Message"
                      multiline
                      rows={2}
                      value={settings.maintenanceMessage}
                      onChange={(e) => handleSettingChange('maintenanceMessage', e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </motion.div>

        {/* Competition Dates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule sx={{ mr: 1 }} />
                Competition Timeline
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Competition Start Date"
                    type="datetime-local"
                    value={settings.startDate}
                    onChange={(e) => handleSettingChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Competition End Date"
                    type="datetime-local"
                    value={settings.endDate}
                    onChange={(e) => handleSettingChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Registration Deadline"
                    type="datetime-local"
                    value={settings.registrationDeadline}
                    onChange={(e) => handleSettingChange('registrationDeadline', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </motion.div>

        {/* Prize Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <MonetizationOn sx={{ mr: 1 }} />
                Prize Configuration
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                {/* XM Points Prizes */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    XM Global - Top 10 Points Prizes
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(settings.xmPointsPrizes).map(([position, prize]) => (
                      <Grid item xs={6} md={3} key={position}>
                        <TextField
                          fullWidth
                          label={`${position.charAt(0).toUpperCase() + position.slice(1)} Place`}
                          value={prize}
                          onChange={(e) => handleSettingChange(`xmPointsPrizes.${position}`, e.target.value)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider />

                {/* XM Percentage Prizes */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    XM Global - Percentage Growth Prizes
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(settings.xmPercentagePrizes).map(([position, prize]) => (
                      <Grid item xs={4} key={position}>
                        <TextField
                          fullWidth
                          label={`${position.charAt(0).toUpperCase() + position.slice(1)} Place`}
                          value={prize}
                          onChange={(e) => handleSettingChange(`xmPercentagePrizes.${position}`, e.target.value)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider />

                {/* Weltrade Prizes */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Weltrade - Volume Traded Prizes
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(settings.weltradePrizes).map(([position, prize]) => (
                      <Grid item xs={6} md={3} key={position}>
                        <TextField
                          fullWidth
                          label={`${position.charAt(0).toUpperCase() + position.slice(1)} Place`}
                          value={prize}
                          onChange={(e) => handleSettingChange(`weltradePrizes.${position}`, e.target.value)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider />

                {/* Monthly Excellence */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Monthly Excellence Award
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Monthly Excellence Prize"
                        value={settings.monthlyExcellencePrize}
                        onChange={(e) => handleSettingChange('monthlyExcellencePrize', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </motion.div>

        {/* System Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <Settings sx={{ mr: 1 }} />
                System & Contact Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support Email"
                    value={settings.supportEmail}
                    onChange={(e) => handleSettingChange('supportEmail', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp Number"
                    value={settings.whatsappNumber}
                    onChange={(e) => handleSettingChange('whatsappNumber', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Maximum Registrations"
                    type="number"
                    value={settings.maxRegistrations}
                    onChange={(e) => handleSettingChange('maxRegistrations', parseInt(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Minimum Trading Days"
                    type="number"
                    value={settings.minTradingDays}
                    onChange={(e) => handleSettingChange('minTradingDays', parseInt(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={settings.emailNotifications}
                          onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                        />
                      }
                      label="Email Notifications Enabled"
                    />
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={settings.verificationRequired}
                          onChange={(e) => handleSettingChange('verificationRequired', e.target.checked)}
                        />
                      }
                      label="Account Verification Required"
                    />
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={settings.autoLeaderboardUpdate}
                          onChange={(e) => handleSettingChange('autoLeaderboardUpdate', e.target.checked)}
                        />
                      }
                      label="Auto Leaderboard Updates"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </motion.div>

        {/* Messages & Announcements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <Info sx={{ mr: 1 }} />
                Messages & Announcements
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Welcome Message"
                  multiline
                  rows={3}
                  value={settings.welcomeMessage}
                  onChange={(e) => handleSettingChange('welcomeMessage', e.target.value)}
                  helperText="Message shown to new users on the homepage"
                />
                
                <Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settings.announcementActive}
                        onChange={(e) => handleSettingChange('announcementActive', e.target.checked)}
                      />
                    }
                    label="Show Site-wide Announcement"
                  />
                  {settings.announcementActive && (
                    <TextField
                      fullWidth
                      label="Announcement Text"
                      multiline
                      rows={3}
                      value={settings.announcementText}
                      onChange={(e) => handleSettingChange('announcementText', e.target.value)}
                      sx={{ mt: 2 }}
                      helperText="This will be displayed as a banner across the site"
                    />
                  )}
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </motion.div>
      </Stack>

      {/* Save Button at Bottom */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          startIcon={<Save />}
          variant="contained"
          size="large"
          onClick={saveSettings}
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompetitionSettings; 